import {
  type InfiniteData,
  type UseMutationOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { matchParam } from '@/utils/tanstackQuery';

import {
  addByomCredential,
  addFavorite,
  addMachineSources,
  addMachineTeams,
  addSourceTeams,
  addSourceToDocument,
  addSourceUsers,
  addTagToContent,
  addTeamMembers,
  addTeamSources,
  addUserSources,
  addUserTeams,
  bulkDeleteInvitations,
  bulkDeleteMachines,
  bulkDeletePATs,
  bulkDeleteSources,
  bulkDeleteTeams,
  bulkRemoveUsersFromOrganization,
  bulkUpdateSourceVisibility,
  bulkUpdateUsersRole,
  clearAgentConnectionCredential,
  connectGitProviderAppRepos,
  createGitProviderApp,
  createMachine,
  createOrganizationInvitations,
  // PAT API functions
  createPAT,
  createScope,
  createSsoConnection,
  createTag,
  createTeam,
  deleteByomCredential,
  deleteDocument,
  deleteGitProviderApp,
  deleteGitProviderAppInstall,
  deleteInvitation,
  deleteMachine,
  deletePAT,
  deleteScope,
  deleteSupportConversation,
  deleteTag,
  deleteTeam,
  deleteTicketProviderInstallation,
  disableByomTeam,
  disableScim,
  enableByomTeam,
  enableScim,
  estimateCodebaseUsage,
  generateCodebases,
  generateJiraPairingCode,
  generatePrimaryAsset,
  getAboutYouSurveySubmitted,
  getAdminSources,
  getAgentInstance,
  getAgentInstances,
  getAtlasPage,
  getAvailableProjects,
  getBannerMessage,
  getByomCredentialDiscovery,
  getByomModels,
  getByomPriceSuggestion,
  getByomStatus,
  getCharges,
  getCodebaseCard,
  getCodebaseVersions,
  getContent,
  getContentDownloadUrl,
  getContentTags,
  getEstateBranches,
  getEstateSummary,
  getGitProviderAppInstallWebhookInfo,
  getGitProviderAppInstalls,
  getGitProviderAppReposByInstallId,
  getGitProviderApps,
  getInvestigationMetrics,
  getLabs,
  getMachine,
  getMachineSources,
  getMachineTeams,
  // Machine API functions
  getMachines,
  getMe,
  getMfaSettings,
  getOnboardingChecklist,
  getOrganizationInvitations,
  getOrganizationUser,
  getOrganizationUsers,
  getOrganizationVisibility,
  getOrganizations,
  getPageAssets,
  getPageContents,
  getPageSources,
  getPatSettings,
  getPrimaryAsset,
  getScimMapping,
  getScopeCustomFieldNames,
  getScopeDetail,
  getScopes,
  getSourceAutodocs,
  getSourceTeams,
  getSourceUsers,
  getSpMetadata,
  getSsoConnectionStatus,
  getSubscription,
  getSupportAgentReadiness,
  getTagContents,
  getTags,
  getTeam,
  getTeamMembers,
  getTeamSources,
  getTeams,
  getTelemetryStatus,
  getTicketProviderInstallations,
  getUsageBalance,
  getUsageTrackingActivity,
  getUsageTrackingByCodebase,
  getUsageTrackingByMachine,
  getUsageTrackingByService,
  getUsageTrackingByTool,
  getUsageTrackingByUser,
  getUsageTrackingEngagement,
  getUsageTrackingEngagementMetrics,
  getUsageTrackingStickiness,
  getUsageTrackingSummary,
  getUserSources,
  getUserTeams,
  getVersions,
  listCodebaseBranches,
  listPATs,
  listSupportConversationFeedback,
  listSupportConversations,
  refreshProjects,
  regenerateMachineKey,
  registerGitProviderWebhook,
  removeFavorite,
  removeMachineSources,
  removeMachineTeams,
  removeSourceFromDocument,
  removeSourceTeams,
  removeSourceUsers,
  removeTagFromContent,
  removeTeamMembers,
  removeTeamSources,
  removeUserFromOrganization,
  removeUserSources,
  removeUserTeams,
  resetPassword,
  saveAgentConnectionCredential,
  saveGitProviderAccessToken,
  setByomOverrides,
  signup,
  skipOnboardingStep,
  startGitHubAuthorization,
  startSupportConversationSuccessor,
  submitAboutYouSurvey,
  submitSupportConversationFeedback,
  testAgentConnection,
  testScimConnection,
  unregisterGitProviderWebhook,
  unskipOnboardingStep,
  updateByomLimits,
  updateContent,
  updateGitProviderGroupAccessToken,
  updateLab,
  updateMachine,
  updateMachineSources,
  updateMachineTeams,
  updateMfaSettings,
  updateOrganizationVisibility,
  updatePatSettings,
  updatePrimaryAsset,
  updateScimMapping,
  updateScope,
  updateScopeStatus,
  updateSourceTeams,
  updateSourceUsers,
  updateSupportConversation,
  updateTag,
  updateTeam,
  updateTeamMembers,
  updateTeamSources,
  updateUserSources,
  updateUserTeams,
  uploadAsset,
  uploadSupportAttachment,
} from './api-axios';
import type { GitProviderApp, GitProviderAppInstall } from './types/gitProvider';
import type { CodebaseCardResponse, FetchPrimaryAssetParams, PrimaryAssetResponse } from './types/node';
import { hasReadableCodebase } from './types/supportAgent';

export const QUERY_KEY = {
  getPrimaryAsset: 'getPrimaryAsset',
  getPageAssets: 'getPageAssets',
  getVersions: 'getVersions',
  getTags: 'getTags',
  getPageSources: 'getPageSources',
  getContentTags: 'getContentTags',
  getTagContents: 'getTagContents',
  getContent: 'getContent',
  getPageContents: 'getPageContents',
  getContentDownloadUrl: 'getContentDownloadUrl',
  getCodebaseCard: 'getCodebaseCard',
  getCodebaseVersions: 'getCodebaseVersions',
  getOrganizationUser: 'getOrganizationUser',
  getOrganizationUsers: 'getOrganizationUsers',
  getOrganizationVisibility: 'getOrganizationVisibility',
  getOrganizationInvitations: 'getOrganizationInvitations',
  getUsageBalance: 'getUsageBalance',
  getSubscription: 'getSubscription',
  getOrganizations: 'getOrganizations',
  getTeam: 'getTeam',
  getTeams: 'getTeams',
  getTeamMembers: 'getTeamMembers',
  getSourceUsers: 'getSourceUsers',
  getSourceTeams: 'getSourceTeams',
  getTeamSources: 'getTeamCodebases',
  getUserSources: 'getUserCodebases',
  getUserTeams: 'getUserTeams',
  searchOrganizationCodebases: 'searchOrganizationCodebases',
  getCharges: 'getCharges',
  getGitProviderApps: 'getGitProviderApps',
  getGitProviderAppInstalls: 'getGitProviderAppInstalls',
  getGitProviderAppInstallWebhookInfo: 'getGitProviderAppInstallWebhookInfo',
  getGitProviderAppReposByInstallId: 'getGitProviderAppReposByInstallId',
  getAboutYouSurveySubmitted: 'getAboutYouSurveySubmitted',
  getAdminSources: 'getAdminSources',
  getMe: 'getMe',
  getOnboardingChecklist: 'getOnboardingChecklist',
  getSourceAutodocs: 'getSourceAutodocs',
  listCodebaseBranches: 'listCodebaseBranches',
  estimateCodebaseUsage: 'estimateCodebaseUsage',
  getEstateSummary: 'getEstateSummary',
  getEstateBranches: 'getEstateBranches',
  getAtlasPage: 'getAtlasPage',
  // Labs query keys
  getLabs: 'getLabs',
  // Machine query keys
  getMachines: 'getMachines',
  getMachine: 'getMachine',
  getMachineTeams: 'getMachineTeams',
  getMachineSources: 'getMachineSources',
  // PAT query keys
  listPATs: 'listPATs',
  patSettings: 'patSettings',
  mfaSettings: 'mfaSettings',
  // Telemetry query keys
  getTelemetryStatus: 'getTelemetryStatus',
  getUsageTrackingSummary: 'getUsageTrackingSummary',
  getUsageTrackingActivity: 'getUsageTrackingActivity',
  getUsageTrackingStickiness: 'getUsageTrackingStickiness',
  getUsageTrackingByTool: 'getUsageTrackingByTool',
  getUsageTrackingByUser: 'getUsageTrackingByUser',
  getUsageTrackingByCodebase: 'getUsageTrackingByCodebase',
  getUsageTrackingByService: 'getUsageTrackingByService',
  getUsageTrackingEngagementMetrics: 'getUsageTrackingEngagementMetrics',
  getUsageTrackingEngagement: 'getUsageTrackingEngagement',
  getUsageTrackingByMachine: 'getUsageTrackingByMachine',
  // Ticket Provider query keys
  getTicketProviderInstallations: 'getTicketProviderInstallations',
  // Ticket Project Scope query keys
  getScopes: 'getScopes',
  getScopeDetail: 'getScopeDetail',
  getScopeCustomFieldNames: 'getScopeCustomFieldNames',
  getAvailableProjects: 'getAvailableProjects',
  // SSO/SCIM query keys
  getSsoConnectionStatus: 'getSsoConnectionStatus',
  getScimMapping: 'getScimMapping',
  getSpMetadata: 'getSpMetadata',
  // BYOM (LiteLLM team) query keys
  getByomStatus: 'getByomStatus',
  getByomModels: 'getByomModels',
  getByomPriceSuggestion: 'getByomPriceSuggestion',
  getByomCredentialDiscovery: 'getByomCredentialDiscovery',
  // AgentCore query keys
  getAgentInstances: 'getAgentInstances',
  getAgentInstance: 'getAgentInstance',
  listSupportConversations: 'listSupportConversations',
  getSupportAgentReadiness: 'getSupportAgentReadiness',
  getInvestigationMetrics: 'getInvestigationMetrics',
  investigationDetail: 'investigationDetail',
  supportConversationFeedback: 'supportConversationFeedback',
} as const;

// TODO: See if there is a more efficient way to create these wrapper hooks.
export const useGetPrimaryAssetQuery = (
  params: Parameters<typeof getPrimaryAsset>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getPrimaryAsset>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getPrimaryAsset, params],
    queryFn: () => getPrimaryAsset(params),
    ...options,
  });

export const useGetPageAssetsQuery = (
  params: Parameters<typeof getPageAssets>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getPageAssets>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getPageAssets, params],
    queryFn: () => getPageAssets(params),
    ...options,
  });

export const useUpdateTeamSourcesMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateTeamSources>>,
      Error,
      Parameters<typeof updateTeamSources>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getTeamSources>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTeamSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamSources],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddTeamSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addTeamSources>>, Error, Parameters<typeof addTeamSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addTeamSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamSources],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveTeamSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeTeamSources>>, Error, Parameters<typeof removeTeamSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeTeamSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamSources],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetCodebaseCardQuery = (
  params: Parameters<typeof getCodebaseCard>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getCodebaseCard>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getCodebaseCard, params],
    queryFn: () => getCodebaseCard(params),
    ...options,
  });

const CODEBASE_CARD_INFINITE_QUERY_KEY = 'codebase-card-infinite' as const;

function getCodebaseCardQueryParams(key: readonly unknown[]): FetchPrimaryAssetParams | undefined {
  if (key[1] === CODEBASE_CARD_INFINITE_QUERY_KEY) {
    return key[2] as FetchPrimaryAssetParams | undefined;
  }
  return key[1] as FetchPrimaryAssetParams | undefined;
}

function isCodebaseCardInfiniteQueryKey(key: readonly unknown[]): boolean {
  return key[1] === CODEBASE_CARD_INFINITE_QUERY_KEY;
}

function optimisticallyUpdateCodebaseCardPage(
  page: CodebaseCardResponse,
  id: string,
  isFavorited: boolean,
  isFavoritesOnlyCache: boolean
): CodebaseCardResponse {
  if (isFavoritesOnlyCache) {
    return {
      ...page,
      results: page.results.filter((r) => r.id !== id),
      total_count: Math.max(0, page.total_count - 1),
    };
  }
  return {
    ...page,
    results: page.results.map((r) => (r.id === id ? { ...r, is_user_favorited: !isFavorited } : r)),
  };
}

// Infinite-scroll variant — each page fetches `params.limit` items starting at `offset: pageParam`.
// `getNextPageParam` advances offset by the accumulated row count and returns undefined when all
// rows have been loaded (total_count reached).
export const useGetCodebaseCardInfiniteQuery = (
  params: Parameters<typeof getCodebaseCard>[0],
  options?: { enabled?: boolean }
) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEY.getCodebaseCard, CODEBASE_CARD_INFINITE_QUERY_KEY, params],
    queryFn: ({ pageParam }) => getCodebaseCard({ ...params, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // An empty page means no further rows at this offset — stop even when total_count
      // still exceeds loaded, otherwise we'd re-request the same offset forever.
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    ...options,
  });

export const useToggleFavoriteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? removeFavorite(id) : addFavorite(id),
    onMutate: async ({ id, isFavorited }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      const previousCards = queryClient.getQueriesData<CodebaseCardResponse | InfiniteData<CodebaseCardResponse>>({
        queryKey: [QUERY_KEY.getCodebaseCard],
      });
      const previousPrimaryAssets = queryClient.getQueriesData<PrimaryAssetResponse>({
        queryKey: [QUERY_KEY.getPrimaryAsset],
      });
      // favorites_only caches need special handling so the sidebar stays
      // logically consistent mid-mutation. Other caches just flip the flag.
      // Infinite-query caches nest pages under `{ pages }` — never read `old.results` directly.
      previousCards.forEach(([key, old]) => {
        if (!old) return;
        const params = getCodebaseCardQueryParams(key);
        const isFavoritesOnlyCache = params?.favorites_only === true;
        if (isFavoritesOnlyCache && !isFavorited) {
          // Favoriting: skip optimistic insert — we don't have the full
          // CodebaseCardRecord for this id here, and the onSettled refetch
          // will populate the sidebar correctly.
          return;
        }
        if (isCodebaseCardInfiniteQueryKey(key)) {
          if (!('pages' in old)) return;
          queryClient.setQueryData<InfiniteData<CodebaseCardResponse>>(key, {
            ...old,
            pages: old.pages.map((page) =>
              optimisticallyUpdateCodebaseCardPage(page, id, isFavorited, isFavoritesOnlyCache)
            ),
          });
          return;
        }
        if (!('results' in old)) return;
        queryClient.setQueryData<CodebaseCardResponse>(
          key,
          optimisticallyUpdateCodebaseCardPage(old, id, isFavorited, isFavoritesOnlyCache)
        );
      });
      previousPrimaryAssets.forEach(([key, old]) => {
        if (!old) return;
        queryClient.setQueryData<PrimaryAssetResponse>(key, {
          ...old,
          results: old.results.map((r) => (r.id === id ? { ...r, is_user_favorited: !isFavorited } : r)),
        });
      });
      return { previousCards, previousPrimaryAssets };
    },
    onError: (_err, _vars, context) => {
      context?.previousCards?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      context?.previousPrimaryAssets?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
    },
  });
};

export const useUpdatePrimaryAssetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePrimaryAsset,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOrganizationUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getUserSources] });
    },
  });
};

export const useGetOrganizationVisibilityQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOrganizationVisibility>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOrganizationVisibility],
    queryFn: getOrganizationVisibility,
    ...options,
  });

export const useUpdateOrganizationVisibilityMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateOrganizationVisibility>>,
      Error,
      Parameters<typeof updateOrganizationVisibility>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOrganizationVisibility,
    ...options,
    onSuccess: (...args) => {
      // Changing the organization visibility has big implications, so easier to invalidate all queries.
      queryClient.invalidateQueries({ predicate: () => true });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetVersionsQuery = (
  params: Parameters<typeof getVersions>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getVersions>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getVersions, params],
    queryFn: () => getVersions(params),
    ...options,
  });

export const useGetTagsQuery = (
  params?: Parameters<typeof getTags>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTags>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTags, params],
    queryFn: () => getTags(params),
    ...options,
  });

export const useGetPageSourcesQuery = (
  params: Parameters<typeof getPageSources>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getPageSources>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getPageSources, params],
    queryFn: () => getPageSources(params),
    ...options,
  });

export const useAddSourceToDocumentMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof addSourceToDocument>>,
      Error,
      Parameters<typeof addSourceToDocument>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: addSourceToDocument,
    ...options,
  });

export const useGenerateCodebasesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof generateCodebases>>, Error, Parameters<typeof generateCodebases>[0]>,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: generateCodebases,
    ...options,
  });

export const useGeneratePrimaryAssetMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof generatePrimaryAsset>>,
      Error,
      Parameters<typeof generatePrimaryAsset>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: generatePrimaryAsset,
    ...options,
  });

export const useRemoveSourceFromDocumentMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof removeSourceFromDocument>>,
      Error,
      Parameters<typeof removeSourceFromDocument>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: removeSourceFromDocument,
    ...options,
  });

export const useCreateTagMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof createTag>>, Error, Parameters<typeof createTag>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTag,
    ...options,
    onSuccess: (...args) => {
      // Invalidate tags list after creating a new tag
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTags] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddTagToContentMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addTagToContent>>, Error, Parameters<typeof addTagToContent>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addTagToContent,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after adding tag to content
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getContentTags] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateTagMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateTag>>, Error, Parameters<typeof updateTag>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTag,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after updating tag
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getContentTags] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTags] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteDocumentMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteDocument>>, Error, Parameters<typeof deleteDocument>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocument,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after deleting the document.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPageAssets] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTelemetryStatus] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useBulkDeleteSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof bulkDeleteSources>>, Error, Parameters<typeof bulkDeleteSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteSources,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPageAssets] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTelemetryStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useBulkUpdateSourceVisibilityMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof bulkUpdateSourceVisibility>>,
      Error,
      Parameters<typeof bulkUpdateSourceVisibility>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkUpdateSourceVisibility,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOrganizationUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getUserSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetContentTagsQuery = (
  params: Parameters<typeof getContentTags>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getContentTags>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getContentTags, params],
    queryFn: () => getContentTags(params),
    ...options,
  });

export const useGetTagContentsQuery = (
  params: Parameters<typeof getTagContents>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTagContents>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTagContents, params],
    queryFn: () => getTagContents(params),
    ...options,
  });

export const useRemoveTagFromContentMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof removeTagFromContent>>,
      Error,
      Parameters<typeof removeTagFromContent>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeTagFromContent,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after removing tag from content
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getContentTags] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteTagMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteTag>>, Error, Parameters<typeof deleteTag>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTag,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after deleting tag
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getContentTags] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTags] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateContentMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateContent>>, Error, Parameters<typeof updateContent>[0]>,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: updateContent,
    ...options,
  });

export const useGetContentQuery = (
  params: Parameters<typeof getContent>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getContent>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getContent, params],
    queryFn: () => getContent(params),
    ...options,
  });

export const useGetPageContentsQuery = (
  params: Parameters<typeof getPageContents>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getPageContents>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getPageContents, params],
    queryFn: () => getPageContents(params),
    ...options,
  });

export const useGetSourceAutodocsQuery = (
  params: Parameters<typeof getSourceAutodocs>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSourceAutodocs>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSourceAutodocs, params],
    queryFn: () => getSourceAutodocs(params),
    ...options,
  });

export const useUploadAssetMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof uploadAsset>>, Error, Parameters<typeof uploadAsset>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadAsset,
    ...options,
    onSuccess: (...args) => {
      // Invalidate relevant queries after uploading the asset.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceAutodocs] });

      // Allow custom onSuccess to still run
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetContentDownloadUrlQuery = (
  params: Parameters<typeof getContentDownloadUrl>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getContentDownloadUrl>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getContentDownloadUrl, params],
    queryFn: () => getContentDownloadUrl(params),
    ...options,
  });

export const useGetCodebaseVersionsQuery = (
  params: Parameters<typeof getCodebaseVersions>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getCodebaseVersions>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getCodebaseVersions, params],
    queryFn: () => getCodebaseVersions(params),
    ...options,
  });

export const useListCodebaseBranchesQuery = (
  params: Parameters<typeof listCodebaseBranches>[0] | undefined,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof listCodebaseBranches>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.listCodebaseBranches, params],
    queryFn: () => listCodebaseBranches(params!),
    enabled: !!params?.codebaseId,
    ...options,
  });

export const useEstimateCodebaseUsageQuery = (
  versionIds: Parameters<typeof estimateCodebaseUsage>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof estimateCodebaseUsage>>, Error>, 'queryKey' | 'queryFn'>
) => {
  // Sort for a stable query key; order is not significant to the API.
  const sortedVersionIds = [...versionIds].sort();
  return useQuery({
    queryKey: [QUERY_KEY.estimateCodebaseUsage, sortedVersionIds],
    queryFn: () => estimateCodebaseUsage(sortedVersionIds),
    enabled: sortedVersionIds.length > 0,
    ...options,
  });
};

export const useGetOrganizationUserQuery = (
  params: Parameters<typeof getOrganizationUser>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOrganizationUser>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOrganizationUser, params],
    queryFn: () => getOrganizationUser(params),
    ...options,
  });

export const useGetOrganizationUsersQuery = (
  params?: Parameters<typeof getOrganizationUsers>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOrganizationUsers>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOrganizationUsers, params],
    queryFn: () => getOrganizationUsers(params),
    ...options,
  });

export const useCreateOrganizationInvitationsMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof createOrganizationInvitations>>,
      Error,
      Parameters<typeof createOrganizationInvitations>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: createOrganizationInvitations,
    ...options,
  });

export const useGetOrganizationInvitationsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOrganizationInvitations>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOrganizationInvitations],
    queryFn: getOrganizationInvitations,
    ...options,
  });

export const useRemoveUserFromOrganizationMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof removeUserFromOrganization>>,
      Error,
      Parameters<typeof removeUserFromOrganization>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: removeUserFromOrganization,
    ...options,
  });

export const useBulkRemoveUsersFromOrganizationMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof bulkRemoveUsersFromOrganization>>,
      Error,
      Parameters<typeof bulkRemoveUsersFromOrganization>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkRemoveUsersFromOrganization,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOrganizationUsers] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteInvitationMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteInvitation>>, Error, Parameters<typeof deleteInvitation>[0]>,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: deleteInvitation,
    ...options,
  });

export const useBulkDeleteInvitationsMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof bulkDeleteInvitations>>,
      Error,
      Parameters<typeof bulkDeleteInvitations>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteInvitations,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOrganizationInvitations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useBulkUpdateUsersRoleMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof bulkUpdateUsersRole>>,
      Error,
      Parameters<typeof bulkUpdateUsersRole>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkUpdateUsersRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOrganizationUsers] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useResetPasswordMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof resetPassword>>, Error>, 'mutationFn'>
) =>
  useMutation({
    mutationFn: resetPassword,
    ...options,
  });

export const useGetUsageBalanceQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageBalance>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageBalance],
    queryFn: getUsageBalance,
    ...options,
  });

export const useGetOrganizationsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOrganizations>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOrganizations],
    queryFn: getOrganizations,
    // Cache this for a decent amount of time since it shouldn't change often.
    staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh
    gcTime: 60 * 60 * 1000, // 1 hour - data stays in cache after unmount
    ...options,
  });

export const useGetTeamsQuery = (
  params: Parameters<typeof getTeams>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTeams>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTeams, params],
    queryFn: () => getTeams(params),
    ...options,
  });

export const useCreateTeamMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof createTeam>>, Error, Parameters<typeof createTeam>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTeam,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetTeamQuery = (
  params: Parameters<typeof getTeam>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTeam>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTeam, params],
    queryFn: () => getTeam(params),
    ...options,
  });

export const useDeleteTeamMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteTeam>>, Error, Parameters<typeof deleteTeam>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTeam,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useBulkDeleteTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof bulkDeleteTeams>>, Error, Parameters<typeof bulkDeleteTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteTeams,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateTeamMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateTeam>>, Error, Parameters<typeof updateTeam>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTeam,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeam],
        predicate: matchParam('teamId', variables.teamId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetTeamMembersQuery = (
  params: Parameters<typeof getTeamMembers>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTeamMembers>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTeamMembers, params],
    queryFn: () => getTeamMembers(params),
    ...options,
  });

export const useGetUserTeamsQuery = (
  params: Parameters<typeof getUserTeams>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUserTeams>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUserTeams, params],
    queryFn: () => getUserTeams(params),
    ...options,
  });

export const useGetSourceTeamsQuery = (
  params: Parameters<typeof getSourceTeams>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSourceTeams>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSourceTeams, params],
    queryFn: () => getSourceTeams(params),
    ...options,
  });

export const useGetSourceUsersQuery = (
  params: Parameters<typeof getSourceUsers>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSourceUsers>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSourceUsers, params],
    queryFn: () => getSourceUsers(params),
    ...options,
  });

export const useUpdateTeamMembersMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateTeamMembers>>,
      Error,
      Parameters<typeof updateTeamMembers>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getTeamMembers>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTeamMembers,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamMembers],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateUserTeamsMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateUserTeams>>,
      Error,
      Parameters<typeof updateUserTeams>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getUserTeams>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserTeams,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserTeams],
        predicate: matchParam('userId', variables.userId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateSourceTeamsMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateSourceTeams>>,
      Error,
      Parameters<typeof updateSourceTeams>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getSourceTeams>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSourceTeams,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateSourceUsersMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateSourceUsers>>,
      Error,
      Parameters<typeof updateSourceUsers>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getSourceUsers>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSourceUsers,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddTeamMembersMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addTeamMembers>>, Error, Parameters<typeof addTeamMembers>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addTeamMembers,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamMembers],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddUserTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addUserTeams>>, Error, Parameters<typeof addUserTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUserTeams,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserTeams],
        predicate: matchParam('userId', variables.user_id),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateUserSourcesMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateUserSources>>,
      Error,
      Parameters<typeof updateUserSources>[0],
      { previousData: Array<[readonly unknown[], Awaited<ReturnType<typeof getUserSources>> | undefined]> }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUserSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserSources],
        predicate: matchParam('userId', variables.userId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddSourceTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addSourceTeams>>, Error, Parameters<typeof addSourceTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addSourceTeams,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddSourceUsersMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addSourceUsers>>, Error, Parameters<typeof addSourceUsers>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addSourceUsers,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveTeamMembersMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeTeamMembers>>, Error, Parameters<typeof removeTeamMembers>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeTeamMembers,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      // Invalidate queries for the specific team.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTeamMembers],
        predicate: matchParam('teamId', variables.teamId),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTeams] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveUserTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeUserTeams>>, Error, Parameters<typeof removeUserTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeUserTeams,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserTeams],
        predicate: matchParam('userId', variables.userId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddUserSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addUserSources>>, Error, Parameters<typeof addUserSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUserSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserSources],
        predicate: matchParam('userId', variables.userId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveUserSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeUserSources>>, Error, Parameters<typeof removeUserSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeUserSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getUserSources],
        predicate: matchParam('userId', variables.userId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveSourceTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeSourceTeams>>, Error, Parameters<typeof removeSourceTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeSourceTeams,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveSourceUsersMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeSourceUsers>>, Error, Parameters<typeof removeSourceUsers>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeSourceUsers,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSourceUsers] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAdminSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetTeamSourcesQuery = (
  params: Parameters<typeof getTeamSources>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTeamSources>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTeamSources, params],
    queryFn: () => getTeamSources(params),
    ...options,
  });

export const useGetUserSourcesQuery = (
  params: Parameters<typeof getUserSources>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUserSources>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUserSources, params],
    queryFn: () => getUserSources(params),
    ...options,
  });

export const useGetSubscriptionQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSubscription],
    queryFn: getSubscription,
    ...options,
  });

export const useGetChargesQuery = (
  params: Parameters<typeof getCharges>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getCharges>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getCharges, params],
    queryFn: () => getCharges(params),
    ...options,
  });

export const useGetGitProviderAppsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getGitProviderApps>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getGitProviderApps],
    queryFn: getGitProviderApps,
    ...options,
  });

export const useGetGitProviderAppInstallsQuery = (
  params: Parameters<typeof getGitProviderAppInstalls>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getGitProviderAppInstalls>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getGitProviderAppInstalls, params],
    queryFn: () => getGitProviderAppInstalls(params),
    ...options,
  });

// Backend commits the new codebases asynchronously: an immediate refetch
// after the connect mutation often returns the old list because the row
// hasn't been written yet. We cascade re-invalidations at increasing
// offsets to catch the row whenever it lands. TelemetryProvider's
// predicate-based invalidator takes over for any window beyond the cascade
// (telemetry polls every 10s and only invalidates queries whose batched
// ids actually changed), so even multiple invalidations of the same query
// only refetch the currently-mounted variant.
//
// Exported for testability — fake timers in tests advance these specific
// delays.
export const POST_CONNECT_REINVALIDATION_DELAYS_MS = [2_000, 5_000, 10_000];

export const useConnectGitProviderAppReposMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof connectGitProviderAppRepos>>,
      Error,
      Parameters<typeof connectGitProviderAppRepos>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  // Track scheduled re-invalidation timers so we can clear them if the
  // hook unmounts before the cascade finishes. Without this cleanup,
  // navigating away mid-cascade (or firing connect twice) leaves
  // overlapping invalidations running. Practically harmless because
  // queryClient is app-scoped, but it leaks across tests and noisy in
  // dev tools.
  const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  useEffect(() => {
    const timers = pendingTimersRef.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  return useMutation({
    mutationFn: connectGitProviderAppRepos,
    ...options,
    onSuccess: (...args) => {
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getPrimaryAsset] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getCodebaseCard] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTelemetryStatus] });
      };
      invalidate();
      for (const delay of POST_CONNECT_REINVALIDATION_DELAYS_MS) {
        const timer = setTimeout(() => {
          pendingTimersRef.current.delete(timer);
          invalidate();
        }, delay);
        pendingTimersRef.current.add(timer);
      }
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteGitProviderAppInstallMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof deleteGitProviderAppInstall>>,
      Error,
      Parameters<typeof deleteGitProviderAppInstall>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGitProviderAppInstall,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      const queryKey = [QUERY_KEY.getGitProviderAppInstalls, { app_id: variables.app_id }];
      queryClient.setQueryData<GitProviderAppInstall[]>(queryKey, (oldData) =>
        oldData ? oldData.filter((install) => install.id !== variables.install_id) : []
      );
      options?.onSuccess?.(...args);
    },
  });
};

export const useSaveGitProviderAccessTokenMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof saveGitProviderAccessToken>>,
      Error,
      Parameters<typeof saveGitProviderAccessToken>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveGitProviderAccessToken,
    ...options,
    onSuccess: (...args) => {
      const [newInstall, variables] = args;
      const queryKey = [QUERY_KEY.getGitProviderAppInstalls, { app_id: variables.app_id }];
      queryClient.setQueryData<GitProviderAppInstall[]>(queryKey, (oldData) =>
        oldData ? [...oldData, newInstall] : [newInstall]
      );
      options?.onSuccess?.(...args);
    },
  });
};

export const useCreateGitProviderAppMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof createGitProviderApp>>,
      Error,
      Parameters<typeof createGitProviderApp>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGitProviderApp,
    ...options,
    onSuccess: (...args) => {
      const [newApp] = args;
      queryClient.setQueryData<GitProviderApp[]>([QUERY_KEY.getGitProviderApps], (oldData) =>
        oldData ? [...oldData, newApp] : [newApp]
      );
      options?.onSuccess?.(...args);
    },
  });
};

export const useStartGitHubAuthorizationMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof startGitHubAuthorization>>, Error, void>, 'mutationFn'>
) => {
  return useMutation({
    mutationFn: startGitHubAuthorization,
    ...options,
  });
};

export const useDeleteGitProviderAppMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof deleteGitProviderApp>>,
      Error,
      Parameters<typeof deleteGitProviderApp>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGitProviderApp,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.setQueryData<GitProviderApp[]>([QUERY_KEY.getGitProviderApps], (oldData) =>
        oldData ? oldData.filter((app) => app.id !== variables.app_id) : []
      );
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetGitProviderAppInstallWebhookInfoQuery = (
  params: Parameters<typeof getGitProviderAppInstallWebhookInfo>[0],
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getGitProviderAppInstallWebhookInfo>>, Error>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: [QUERY_KEY.getGitProviderAppInstallWebhookInfo, params],
    queryFn: () => getGitProviderAppInstallWebhookInfo(params),
    ...options,
  });

export const useRegisterGitProviderWebhookMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof registerGitProviderWebhook>>,
      Error,
      Parameters<typeof registerGitProviderWebhook>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: registerGitProviderWebhook,
    ...options,
    onSuccess: (data, variables, ...args) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getGitProviderAppInstalls],
        predicate: matchParam('app_id', variables.app_id),
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getGitProviderAppInstallWebhookInfo],
      });
      options?.onSuccess?.(data, variables, ...args);
    },
  });
};

export const useUnregisterGitProviderWebhookMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof unregisterGitProviderWebhook>>,
      Error,
      Parameters<typeof unregisterGitProviderWebhook>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unregisterGitProviderWebhook,
    ...options,
    onSuccess: (data, variables, ...args) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getGitProviderAppInstalls],
        predicate: matchParam('app_id', variables.app_id),
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getGitProviderAppInstallWebhookInfo],
      });
      options?.onSuccess?.(data, variables, ...args);
    },
  });
};

export const useGetGitProviderAppReposByInstallIdQuery = (
  params: Parameters<typeof getGitProviderAppReposByInstallId>[0],
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getGitProviderAppReposByInstallId>>, Error>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: [QUERY_KEY.getGitProviderAppReposByInstallId, params],
    queryFn: () => getGitProviderAppReposByInstallId(params),
    ...options,
  });

export const useUpdateGitProviderGroupAccessTokenMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateGitProviderGroupAccessToken>>,
      Error,
      Parameters<typeof updateGitProviderGroupAccessToken>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGitProviderGroupAccessToken,
    ...options,
    onSuccess: (...args) => {
      const [updatedInstall, variables] = args;
      const queryKey = [QUERY_KEY.getGitProviderAppInstalls, { app_id: variables.app_id }];
      queryClient.setQueryData<GitProviderAppInstall[]>(queryKey, (oldData) =>
        oldData
          ? oldData.map((install) => (install.id === variables.install_id ? updatedInstall : install))
          : [updatedInstall]
      );
      options?.onSuccess?.(...args);
    },
  });
};

export const useSignupMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof signup>>, Error, Parameters<typeof signup>[0]>,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: signup,
    ...options,
  });

export const useSubmitAboutYouSurveyMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof submitAboutYouSurvey>>,
      Error,
      Parameters<typeof submitAboutYouSurvey>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitAboutYouSurvey,
    ...options,
    onSuccess: (...args) => {
      queryClient.setQueryData([QUERY_KEY.getAboutYouSurveySubmitted], { submitted: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAboutYouSurveySubmitted] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetAboutYouSurveySubmittedQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAboutYouSurveySubmitted>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getAboutYouSurveySubmitted],
    queryFn: getAboutYouSurveySubmitted,
    ...options,
  });

export const useGetOnboardingChecklistQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getOnboardingChecklist>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getOnboardingChecklist],
    queryFn: getOnboardingChecklist,
    ...options,
  });

export const useSkipOnboardingStepMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: skipOnboardingStep,
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY.getOnboardingChecklist], data);
    },
  });
};

export const useUnskipOnboardingStepMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unskipOnboardingStep,
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY.getOnboardingChecklist], data);
    },
  });
};

export const useGetAdminSourcesQuery = (
  params: Parameters<typeof getAdminSources>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAdminSources>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getAdminSources, params],
    queryFn: () => getAdminSources(params),
    ...options,
  });

export const useGetMeQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getMe],
    queryFn: getMe,
    ...options,
  });

export const useGetEstateSummaryQuery = (
  params: Parameters<typeof getEstateSummary>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getEstateSummary>>, Error>, 'queryKey' | 'queryFn'>
) => useQuery({ queryKey: [QUERY_KEY.getEstateSummary, params], queryFn: () => getEstateSummary(params), ...options });

export const useGetEstateBranchesQuery = (
  params: Parameters<typeof getEstateBranches>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getEstateBranches>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({ queryKey: [QUERY_KEY.getEstateBranches, params], queryFn: () => getEstateBranches(params), ...options });

export const useGetAtlasPageQuery = (
  params: Parameters<typeof getAtlasPage>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAtlasPage>>, Error>, 'queryKey' | 'queryFn'>
) => useQuery({ queryKey: [QUERY_KEY.getAtlasPage, params], queryFn: () => getAtlasPage(params), ...options });

export const useGetBannerMessageQuery = () =>
  useQuery<{ message: string } | null>({
    queryKey: ['bannerMessage'],
    queryFn: getBannerMessage,
    retry: false,
    refetchOnWindowFocus: false,
  });

// ===== Machine Hooks =====

export const useGetMachinesQuery = (
  params: Parameters<typeof getMachines>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMachines>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getMachines, params],
    queryFn: () => getMachines(params),
    ...options,
  });

export const useGetMachineQuery = (
  params: Parameters<typeof getMachine>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMachine>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getMachine, params],
    queryFn: () => getMachine(params),
    ...options,
  });

export const useCreateMachineMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof createMachine>>, Error, Parameters<typeof createMachine>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMachine,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachines] });
      options?.onSuccess?.(...args);
    },
  });
};

type ListMachinesData = Awaited<ReturnType<typeof getMachines>>;
type MachinesCaches = Map<readonly unknown[], ListMachinesData | undefined>;

function optimisticallyUpdateMachines(
  queryClient: ReturnType<typeof useQueryClient>,
  updater: (machines: ListMachinesData['machines']) => ListMachinesData['machines']
) {
  const allCaches = queryClient.getQueriesData<ListMachinesData>({ queryKey: [QUERY_KEY.getMachines] });
  const previousCaches: MachinesCaches = new Map(allCaches);
  allCaches.forEach(([key, old]) => {
    if (!old) return;
    const updated = updater(old.machines);
    queryClient.setQueryData<ListMachinesData>(key, {
      ...old,
      machines: updated,
      total: old.total - (old.machines.length - updated.length),
    });
  });
  return previousCaches;
}

function rollbackMachinesCaches(queryClient: ReturnType<typeof useQueryClient>, previousCaches: MachinesCaches) {
  previousCaches.forEach((data, key) => {
    queryClient.setQueryData<ListMachinesData>(key, data);
  });
}

export const useUpdateMachineMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateMachine>>, Error, Parameters<typeof updateMachine>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMachine,
    ...options,
    onMutate: async (variables, ctx) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.getMachines] });
      const previousCaches = optimisticallyUpdateMachines(queryClient, (machines) =>
        machines.map((m) => (m.id === variables.machineId ? { ...m, ...variables } : m))
      );
      const upstream = (await options?.onMutate?.(variables, ctx)) ?? {};
      return { ...upstream, _previousMachinesCaches: previousCaches };
    },
    onError: (error, variables, onMutateResult, ctx) => {
      const caches = (onMutateResult as { _previousMachinesCaches?: MachinesCaches } | undefined)
        ?._previousMachinesCaches;
      if (caches) {
        rollbackMachinesCaches(queryClient, caches);
      }
      options?.onError?.(error, variables, onMutateResult, ctx);
    },
    onSettled: (data, error, variables, onMutateResult, ctx) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachines] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      // org_access changes which sources the machine reads, so the Sources tab
      // must not keep its cached list and count.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachineSources],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSettled?.(data, error, variables, onMutateResult, ctx);
    },
  });
};

export const useDeleteMachineMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteMachine>>, Error, Parameters<typeof deleteMachine>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMachine,
    ...options,
    onMutate: async (variables, ctx) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.getMachines] });
      const previousCaches = optimisticallyUpdateMachines(queryClient, (machines) =>
        machines.filter((m) => m.id !== variables.machineId)
      );
      const upstream = (await options?.onMutate?.(variables, ctx)) ?? {};
      return { ...upstream, _previousMachinesCaches: previousCaches };
    },
    onError: (error, variables, onMutateResult, ctx) => {
      const caches = (onMutateResult as { _previousMachinesCaches?: MachinesCaches } | undefined)
        ?._previousMachinesCaches;
      if (caches) {
        rollbackMachinesCaches(queryClient, caches);
      }
      options?.onError?.(error, variables, onMutateResult, ctx);
    },
    onSettled: (data, error, variables, onMutateResult, ctx) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachines] });
      options?.onSettled?.(data, error, variables, onMutateResult, ctx);
    },
  });
};

export const useBulkDeleteMachinesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof bulkDeleteMachines>>, Error, Parameters<typeof bulkDeleteMachines>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteMachines,
    ...options,
    onMutate: async (variables, ctx) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.getMachines] });
      const idsToDelete = new Set(variables.ids);
      const previousCaches = optimisticallyUpdateMachines(queryClient, (machines) =>
        machines.filter((m) => !idsToDelete.has(m.id))
      );
      const upstream = (await options?.onMutate?.(variables, ctx)) ?? {};
      return { ...upstream, _previousMachinesCaches: previousCaches };
    },
    onError: (error, variables, onMutateResult, ctx) => {
      const caches = (onMutateResult as { _previousMachinesCaches?: MachinesCaches } | undefined)
        ?._previousMachinesCaches;
      if (caches) {
        rollbackMachinesCaches(queryClient, caches);
      }
      options?.onError?.(error, variables, onMutateResult, ctx);
    },
    onSettled: (data, error, variables, onMutateResult, ctx) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachines] });
      options?.onSettled?.(data, error, variables, onMutateResult, ctx);
    },
  });
};

export const useRegenerateMachineKeyMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof regenerateMachineKey>>,
      Error,
      Parameters<typeof regenerateMachineKey>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateMachineKey,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachines] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Machine Teams Hooks =====

export const useGetMachineTeamsQuery = (
  params: Parameters<typeof getMachineTeams>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMachineTeams>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getMachineTeams, params],
    queryFn: () => getMachineTeams(params),
    ...options,
  });

export const useAddMachineTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addMachineTeams>>, Error, Parameters<typeof addMachineTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addMachineTeams,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateMachineTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateMachineTeams>>, Error, Parameters<typeof updateMachineTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMachineTeams,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveMachineTeamsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof removeMachineTeams>>, Error, Parameters<typeof removeMachineTeams>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeMachineTeams,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineTeams] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Machine Sources Hooks =====

export const useGetMachineSourcesQuery = (
  params: Parameters<typeof getMachineSources>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMachineSources>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getMachineSources, params],
    queryFn: () => getMachineSources(params),
    ...options,
  });

export const useAddMachineSourcesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addMachineSources>>, Error, Parameters<typeof addMachineSources>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addMachineSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateMachineSourcesMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateMachineSources>>,
      Error,
      Parameters<typeof updateMachineSources>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMachineSources,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRemoveMachineSourcesMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof removeMachineSources>>,
      Error,
      Parameters<typeof removeMachineSources>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeMachineSources,
    ...options,
    onSuccess: (...args) => {
      const [, variables] = args;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMachineSources] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getMachine],
        predicate: matchParam('machineId', variables.machineId),
      });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Labs Hooks =====

export const useGetLabsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getLabs>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getLabs],
    queryFn: getLabs,
    ...options,
  });

export const useUpdateLabMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateLab>>, Error, Parameters<typeof updateLab>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLab,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getLabs] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMe] });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Telemetry Hooks =====

export const useGetTelemetryStatusQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getTelemetryStatus>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTelemetryStatus],
    queryFn: getTelemetryStatus,
    ...options,
  });

// ===== Usage Tracking Hooks =====
export const useGetUsageTrackingSummaryQuery = (
  params: Parameters<typeof getUsageTrackingSummary>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingSummary>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingSummary, params],
    queryFn: () => getUsageTrackingSummary(params),
    ...options,
  });

export const useGetUsageTrackingActivityQuery = (
  params: Parameters<typeof getUsageTrackingActivity>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingActivity>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingActivity, params],
    queryFn: () => getUsageTrackingActivity(params),
    ...options,
  });

export const useGetUsageTrackingStickinessQuery = (
  params: Parameters<typeof getUsageTrackingStickiness>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingStickiness>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingStickiness, params],
    queryFn: () => getUsageTrackingStickiness(params),
    ...options,
  });

export const useGetUsageTrackingByToolQuery = (
  params: Parameters<typeof getUsageTrackingByTool>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingByTool>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingByTool, params],
    queryFn: () => getUsageTrackingByTool(params),
    ...options,
  });

export const useGetUsageTrackingByUserQuery = (
  params: Parameters<typeof getUsageTrackingByUser>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingByUser>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingByUser, params],
    queryFn: () => getUsageTrackingByUser(params),
    ...options,
  });

export const useGetUsageTrackingByCodebaseQuery = (
  params: Parameters<typeof getUsageTrackingByCodebase>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingByCodebase>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingByCodebase, params],
    queryFn: () => getUsageTrackingByCodebase(params),
    ...options,
  });

export const useGetUsageTrackingByServiceQuery = (
  params: Parameters<typeof getUsageTrackingByService>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingByService>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingByService, params],
    queryFn: () => getUsageTrackingByService(params),
    ...options,
  });

export const useGetUsageTrackingEngagementMetricsQuery = (
  params?: Parameters<typeof getUsageTrackingEngagementMetrics>[0],
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingEngagementMetrics>>, Error>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingEngagementMetrics, params],
    queryFn: () => getUsageTrackingEngagementMetrics(params),
    ...options,
  });

export const useGetUsageTrackingEngagementQuery = (
  params: Parameters<typeof getUsageTrackingEngagement>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingEngagement>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingEngagement, params],
    queryFn: () => getUsageTrackingEngagement(params),
    ...options,
  });

export const useGetUsageTrackingByMachineQuery = (
  params: Parameters<typeof getUsageTrackingByMachine>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getUsageTrackingByMachine>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getUsageTrackingByMachine, params],
    queryFn: () => getUsageTrackingByMachine(params),
    ...options,
  });

// ===== Ticket Provider Hooks =====

export const useGetTicketProviderInstallationsQuery = (
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof getTicketProviderInstallations>>, Error>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: [QUERY_KEY.getTicketProviderInstallations],
    queryFn: getTicketProviderInstallations,
    ...options,
  });

export const useDeleteTicketProviderInstallationMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deleteTicketProviderInstallation>>, Error, string>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTicketProviderInstallation,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTicketProviderInstallations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGenerateJiraPairingCodeMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof generateJiraPairingCode>>, Error, void>, 'mutationFn'>
) =>
  useMutation({
    mutationFn: generateJiraPairingCode,
    ...options,
  });

// ===== Personal Access Token Hooks =====

export const useListPATsQuery = (
  params: Parameters<typeof listPATs>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof listPATs>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.listPATs, params],
    queryFn: () => listPATs(params),
    ...options,
  });

export const useCreatePATMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof createPAT>>, Error, Parameters<typeof createPAT>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPAT,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listPATs] });
      options?.onSuccess?.(...args);
    },
  });
};

type ListPATsData = Awaited<ReturnType<typeof listPATs>>;

function optimisticallyDeletePATs(queryClient: ReturnType<typeof useQueryClient>, tokenIds: Set<string>) {
  const allCaches = queryClient.getQueriesData<ListPATsData>({ queryKey: [QUERY_KEY.listPATs] });
  const previousCaches = new Map(allCaches);
  allCaches.forEach(([key, old]) => {
    if (!old) return;
    const remaining = old.tokens.filter((t) => !tokenIds.has(t.id));
    queryClient.setQueryData<ListPATsData>(key, {
      ...old,
      tokens: remaining,
      total: old.total - (old.tokens.length - remaining.length),
    });
  });
  return previousCaches;
}

function rollbackPATCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  previousCaches: Map<readonly unknown[], ListPATsData | undefined>
) {
  previousCaches.forEach((data, key) => {
    queryClient.setQueryData<ListPATsData>(key, data);
  });
}

type DeletePATCaches = Map<readonly unknown[], ListPATsData | undefined>;

export const useDeletePATMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof deletePAT>>, Error, Parameters<typeof deletePAT>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePAT,
    ...options,
    onMutate: async (variables, ctx) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.listPATs] });
      const previousCaches = optimisticallyDeletePATs(queryClient, new Set([variables.tokenId]));
      const upstream = (await options?.onMutate?.(variables, ctx)) ?? {};
      return { ...upstream, _previousCaches: previousCaches };
    },
    onError: (error, variables, onMutateResult, ctx) => {
      const caches = (onMutateResult as { _previousCaches?: DeletePATCaches } | undefined)?._previousCaches;
      if (caches) {
        rollbackPATCaches(queryClient, caches);
      }
      options?.onError?.(error, variables, onMutateResult, ctx);
    },
    onSettled: (data, error, variables, onMutateResult, ctx) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listPATs] });
      options?.onSettled?.(data, error, variables, onMutateResult, ctx);
    },
  });
};

export const useBulkDeletePATsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof bulkDeletePATs>>, Error, Parameters<typeof bulkDeletePATs>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeletePATs,
    ...options,
    onMutate: async (variables, ctx) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY.listPATs] });
      const previousCaches = optimisticallyDeletePATs(queryClient, new Set(variables.ids));
      const upstream = (await options?.onMutate?.(variables, ctx)) ?? {};
      return { ...upstream, _previousCaches: previousCaches };
    },
    onError: (error, variables, onMutateResult, ctx) => {
      const caches = (onMutateResult as { _previousCaches?: DeletePATCaches } | undefined)?._previousCaches;
      if (caches) {
        rollbackPATCaches(queryClient, caches);
      }
      options?.onError?.(error, variables, onMutateResult, ctx);
    },
    onSettled: (data, error, variables, onMutateResult, ctx) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listPATs] });
      options?.onSettled?.(data, error, variables, onMutateResult, ctx);
    },
  });
};

export const useGetPatSettingsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getPatSettings>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.patSettings],
    queryFn: getPatSettings,
    ...options,
  });

export const useUpdatePatSettingsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updatePatSettings>>, Error, Parameters<typeof updatePatSettings>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePatSettings,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.patSettings] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetMfaSettingsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getMfaSettings>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.mfaSettings],
    queryFn: getMfaSettings,
    ...options,
  });

export const useUpdateMfaSettingsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateMfaSettings>>, Error, Parameters<typeof updateMfaSettings>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMfaSettings,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.mfaSettings] });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Ticket Project Scope Hooks =====

export const useGetScopesQuery = (
  installationId: string,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getScopes>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getScopes, installationId],
    queryFn: () => getScopes(installationId),
    ...options,
  });

export const useGetScopeDetailQuery = (
  scopeId: string,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getScopeDetail>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getScopeDetail, scopeId],
    queryFn: () => getScopeDetail(scopeId),
    ...options,
  });

export const useGetScopeCustomFieldNamesQuery = (
  scopeId: string,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getScopeCustomFieldNames>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getScopeCustomFieldNames, scopeId],
    queryFn: () => getScopeCustomFieldNames(scopeId),
    ...options,
  });

export const useGetAvailableProjectsQuery = (
  installationId: string,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAvailableProjects>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getAvailableProjects, installationId],
    queryFn: () => getAvailableProjects(installationId),
    ...options,
  });

export const useCreateScopeMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof createScope>>,
      Error,
      { installationId: string; data: Parameters<typeof createScope>[1] }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ installationId, data }) => createScope(installationId, data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopes] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAvailableProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTicketProviderInstallations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateScopeMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateScope>>,
      Error,
      { scopeId: string; data: Parameters<typeof updateScope>[1] }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scopeId, data }) => updateScope(scopeId, data),
    ...options,
    onMutate: async (variables) => {
      const queryKey = [QUERY_KEY.getScopeDetail, variables.scopeId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Awaited<ReturnType<typeof getScopeDetail>>>(queryKey);
      if (previous) {
        queryClient.setQueryData(queryKey, { ...previous, ...variables.data });
      }
      return { previous };
    },
    onError: (...args) => {
      const context = args[2] as { previous?: Awaited<ReturnType<typeof getScopeDetail>> } | undefined;
      const scopeId = args[1].scopeId;
      if (context?.previous) {
        queryClient.setQueryData([QUERY_KEY.getScopeDetail, scopeId], context.previous);
      }
      options?.onError?.(...args);
    },
    onSettled: (...args) => {
      const scopeId = args[2].scopeId;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopes] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopeDetail, scopeId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTicketProviderInstallations] });
      options?.onSettled?.(...args);
    },
  });
};

export const useUpdateScopeStatusMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateScopeStatus>>, Error, { scopeId: string; status: string }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scopeId, status }) => updateScopeStatus(scopeId, status),
    ...options,
    onSuccess: (...args) => {
      const scopeId = args[1].scopeId;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopes] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopeDetail, scopeId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTicketProviderInstallations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteScopeMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof deleteScope>>, Error, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteScope,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScopes] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAvailableProjects] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getTicketProviderInstallations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useRefreshProjectsMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof refreshProjects>>, Error, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshProjects,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAvailableProjects] });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== SSO / SCIM self-service =====

export const useSsoConnectionStatusQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSsoConnectionStatus>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSsoConnectionStatus],
    queryFn: getSsoConnectionStatus,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.sso_enabled ? false : 5000),
    ...options,
  });

export const useEnableScimMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof enableScim>>, Error, void>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enableScim,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSsoConnectionStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDisableScimMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof disableScim>>, Error, void>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disableScim,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSsoConnectionStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useTestScimConnectionMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof testScimConnection>>, Error, Parameters<typeof testScimConnection>[0]>,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: testScimConnection,
    ...options,
  });

export const useScimMappingQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getScimMapping>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getScimMapping],
    queryFn: getScimMapping,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useUpdateScimMappingMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateScimMapping>>, Error, Parameters<typeof updateScimMapping>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateScimMapping,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getScimMapping] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useSpMetadataQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSpMetadata>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSpMetadata],
    queryFn: getSpMetadata,
    refetchOnWindowFocus: false,
    // SP metadata is computed deterministically from org_id; safe to cache
    // for the duration of the page session.
    staleTime: Infinity,
    ...options,
  });

export const useCreateSsoConnectionMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof createSsoConnection>>,
      Error,
      Parameters<typeof createSsoConnection>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSsoConnection,
    ...options,
    onSuccess: (...args) => {
      // Connection-status flips to configured immediately — invalidate so
      // the UI re-fetches and re-renders without waiting for the next poll.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSsoConnectionStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== Bring Your Own Model (per-org LiteLLM team) =====

export const useByomStatusQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getByomStatus>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getByomStatus],
    queryFn: getByomStatus,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useByomModelsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getByomModels>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getByomModels],
    queryFn: getByomModels,
    refetchOnWindowFocus: false,
    // The default catalog is static for a deploy.
    staleTime: Infinity,
    ...options,
  });

export const useByomPriceSuggestionQuery = (
  params: Parameters<typeof getByomPriceSuggestion>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getByomPriceSuggestion>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getByomPriceSuggestion, params],
    queryFn: () => getByomPriceSuggestion(params),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    ...options,
  });

export const useByomCredentialDiscoveryQuery = (
  params: Parameters<typeof getByomCredentialDiscovery>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getByomCredentialDiscovery>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getByomCredentialDiscovery, params],
    queryFn: () => getByomCredentialDiscovery(params),
    refetchOnWindowFocus: false,
    ...options,
  });

export const useEnableByomTeamMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof enableByomTeam>>, Error, Parameters<typeof enableByomTeam>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enableByomTeam,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDisableByomTeamMutation = (
  options?: Omit<UseMutationOptions<Awaited<ReturnType<typeof disableByomTeam>>, Error, void>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disableByomTeam,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateByomLimitsMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof updateByomLimits>>, Error, Parameters<typeof updateByomLimits>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateByomLimits,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useAddByomCredentialMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof addByomCredential>>, Error, Parameters<typeof addByomCredential>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addByomCredential,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteByomCredentialMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof deleteByomCredential>>,
      Error,
      Parameters<typeof deleteByomCredential>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteByomCredential,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useSetByomOverridesMutation = (
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof setByomOverrides>>, Error, Parameters<typeof setByomOverrides>[0]>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setByomOverrides,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getByomStatus] });
      options?.onSuccess?.(...args);
    },
  });
};

// ===== AgentCore =====

export const useAgentInstancesQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAgentInstances>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getAgentInstances],
    queryFn: getAgentInstances,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useAgentInstanceQuery = (
  params: Parameters<typeof getAgentInstance>[0],
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getAgentInstance>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getAgentInstance, params],
    queryFn: () => getAgentInstance(params),
    refetchOnWindowFocus: false,
    ...options,
  });

export const useSaveAgentConnectionCredentialMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof saveAgentConnectionCredential>>,
      Error,
      Parameters<typeof saveAgentConnectionCredential>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveAgentConnectionCredential,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAgentInstances] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAgentInstance] });
      // A saved ticketing credential completes the checklist's issue-tracker
      // step and turns the Investigations connect-Jira prompt off; neither
      // query refetches on its own.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getOnboardingChecklist] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getSupportAgentReadiness] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useClearAgentConnectionCredentialMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof clearAgentConnectionCredential>>,
      Error,
      Parameters<typeof clearAgentConnectionCredential>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearAgentConnectionCredential,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAgentInstances] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAgentInstance] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useTestAgentConnectionMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof testAgentConnection>>,
      Error,
      Parameters<typeof testAgentConnection>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: testAgentConnection,
    ...options,
  });

// ===== Support Agent =====

// When a codebase has context or a ticketing credential is saved, a new
// organization waits seconds to minutes for converge to build its agent. The
// page polls often during that wait. Every other wait (context generating,
// setup someone must do, an agent ops must fix, or one already serving) takes
// far longer than a slow poll's interval.
const SUPPORT_AGENT_READINESS_SETTLING_INTERVAL = 5_000;
const SUPPORT_AGENT_READINESS_SETTLED_INTERVAL = 60_000;

export const useSupportAgentReadinessQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof getSupportAgentReadiness>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.getSupportAgentReadiness],
    queryFn: getSupportAgentReadiness,
    // A failed read lets the composer through (see deriveWorkspaceReadiness),
    // but only once retries give up; the global three with backoff would hold
    // it at "Checking…" for seconds. The slow poll below recovers instead.
    retry: false,
    refetchInterval: (query) => {
      if (query.state.status === 'error') {
        return SUPPORT_AGENT_READINESS_SETTLED_INTERVAL;
      }
      const readiness = query.state.data;
      if (!readiness?.flag_enabled) {
        return false;
      }
      // The same instance states deriveWorkspaceReadiness shows as provisioning,
      // behind the same precondition: a codebase the agent can read in the
      // organization's mode (connected outside LEGACY), or a ticketing source.
      const isSettling =
        readiness.serving_role === 'none' &&
        (hasReadableCodebase(readiness) || readiness.ticketing_connected === true) &&
        (readiness.instance_status == null ||
          readiness.instance_status === 'provisioning' ||
          readiness.instance_status === 'ready');
      return isSettling ? SUPPORT_AGENT_READINESS_SETTLING_INTERVAL : SUPPORT_AGENT_READINESS_SETTLED_INTERVAL;
    },
    ...options,
  });

export const useListSupportConversationsQuery = (
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof listSupportConversations>>, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery({
    queryKey: [QUERY_KEY.listSupportConversations, { limit: 200 }],
    queryFn: () => listSupportConversations({ limit: 200 }),
    ...options,
  });

export const useUploadSupportAttachmentMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof uploadSupportAttachment>>,
      Error,
      Parameters<typeof uploadSupportAttachment>[0]
    >,
    'mutationFn'
  >
) =>
  useMutation({
    mutationFn: uploadSupportAttachment,
    ...options,
  });

export const useSupportConversationFeedbackQuery = (
  params: Parameters<typeof listSupportConversationFeedback>[0],
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof listSupportConversationFeedback>>, Error>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: [QUERY_KEY.supportConversationFeedback, params],
    queryFn: () => listSupportConversationFeedback(params),
    ...options,
  });

export const useSubmitSupportConversationFeedbackMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof submitSupportConversationFeedback>>,
      Error,
      Parameters<typeof submitSupportConversationFeedback>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitSupportConversationFeedback,
    ...options,
    onSuccess: (...args) => {
      const conversationId = args[1].conversationId;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.supportConversationFeedback, { conversationId }] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateSupportConversationMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateSupportConversation>>,
      Error,
      Parameters<typeof updateSupportConversation>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSupportConversation,
    ...options,
    onSuccess: (...args) => {
      const conversationId = args[1].conversationId;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listSupportConversations] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.investigationDetail, conversationId] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteSupportConversationMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof deleteSupportConversation>>,
      Error,
      Parameters<typeof deleteSupportConversation>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSupportConversation,
    ...options,
    onSuccess: (...args) => {
      const conversationId = args[1].conversationId;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listSupportConversations] });
      queryClient.removeQueries({ queryKey: [QUERY_KEY.investigationDetail, conversationId] });
      // The conversation's feedback rows cascade with it server-side.
      queryClient.removeQueries({ queryKey: [QUERY_KEY.supportConversationFeedback, { conversationId }] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useStartSupportConversationSuccessorMutation = (
  options?: Omit<
    UseMutationOptions<
      Awaited<ReturnType<typeof startSupportConversationSuccessor>>,
      Error,
      Parameters<typeof startSupportConversationSuccessor>[0]
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startSupportConversationSuccessor,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.listSupportConversations] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useGetInvestigationMetricsQuery = (params: Parameters<typeof getInvestigationMetrics>[0]) =>
  useQuery({
    queryKey: [QUERY_KEY.getInvestigationMetrics, params],
    queryFn: () => getInvestigationMetrics(params),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
